import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';

import { TRANSCRIPTION_LANGUAGES } from '@/constants/transcription';
import { TRANSCRIPTION_LANGUAGE_PAGES } from '@/constants/transcriptionLanguagePages';

import { useLandingT } from './kit';

/** Every language with its own page, linked; the current one is marked. */
export default function Languages({ currentSlug }: { currentSlug?: string }) {
  const { t } = useLandingT();

  return (
    <section id='languages' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('{{count}} languages.', {
            count: TRANSCRIPTION_LANGUAGES.length,
          })}
          tail={t('Or let it find the language for you.')}
          lead={t(
            'Choose the language spoken in your recording for the best accuracy, or leave it on automatic detection.',
          )}
        />

        <ul
          role='list'
          className='mt-12 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6'
        >
          {TRANSCRIPTION_LANGUAGE_PAGES.map((language) => {
            const current = language.slug === currentSlug;
            return (
              <li key={language.slug}>
                <Link
                  href={`/ai-transcription/${language.slug}`}
                  aria-current={current ? 'page' : undefined}
                  className={clsxm(
                    'flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors',
                    current
                      ? 'bg-primary-600 text-white'
                      : clsxm(
                          'hover:bg-white hover:text-primary-700 hover:shadow-[0_1px_2px_rgba(15,15,40,0.06)] dark:hover:bg-white/[0.06] dark:hover:text-primary-300',
                          tone.ink,
                        ),
                  )}
                >
                  <span aria-hidden='true' className='text-lg leading-none'>
                    {language.flag}
                  </span>
                  <span className='truncate'>{t(language.name)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
